<template>
    <div>
        <!-- FIRST NAME (SHOWN IF NOT ALSO CREATING WEB USER) -->
        <div class="form-group" v-if="!createWebUser">
            <ValidationProvider
                name="drugSafeUserFirstName"
                :rules="{
                    required: true
                }"
                v-slot="{ errors }"
            >
                <label for="drugSafeUserFirstNameInput">{{ $t('first_name') }}</label>
                <input
                    type="text"
                    id="drugSafeUserFirstNameInput"
                    class="form-control"
                    v-model="drugSafeUser.first_name"
                />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- LAST NAME (SHOWN IF NOT ALSO CREATING WEB USER) -->
        <div class="form-group" v-if="!createWebUser">
            <ValidationProvider
                name="drugSafeUserLastName"
                :rules="{
                    required: true
                }"
                v-slot="{ errors }"
            >
                <label for="drugSafeUserLastNameInput">{{ $t('last_name') }}</label>
                <input
                    type="text"
                    id="drugSafeUserLastNameInput"
                    class="form-control"
                    v-model="drugSafeUser.last_name"
                />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- PHARMACY (SHOWN IF NOT EDITING, USER IS SUPERADMIN, AND NOT ALSO CREATING WEB USER) -->
        <div class="form-group" v-if="!edit && user.permissions.superadmin && !createWebUser">
            <ValidationProvider name="drugSafeUserPharmacy" rules="required" v-slot="{ errors }">
                <label for="drugSafeUserPharmacySelection">{{ $t('pharmacy') }}</label>
                <select
                    id="drugSafeUserPharmacySelection"
                    class="form-control"
                    @change="onChangePharmacy"
                    v-model="drugSafeUser.pharmacy_id"
                >
                    <option disabled>
                        {{ `${$t('select')} ${$t('pharmacy').toLowerCase()}` }}
                    </option>
                    <option v-for="pharmacy in pharmacies" v-bind:key="pharmacy.id" v-bind:value="pharmacy.id">
                        {{ pharmacy.information.name }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- ROLE -->
        <div class="form-group">
            <ValidationProvider name="drugSafeUserRole" :rules="{ required: true }" v-slot="{ errors }">
                <label for="drugSafeUserRoleSelection">{{ $t('role') }}</label>
                <!-- disable field if editing and either target user is superadmin or current user doesn't have editing permissions -->
                <input
                    v-if="edit && (drugSafeUser.role === 'superadmin' || !user.permissions.edit_user)"
                    class="form-control"
                    disabled
                    id="drugSafeUserRoleSelection"
                    :value="$t(`role_${drugSafeUser.role}`)"
                />
                <!-- otherwise show role selection -->
                <select v-else class="form-control" id="drugSafeUserRoleSelection" v-model="drugSafeUser.role">
                    <option disabled>{{ $t('select') }} {{ $t('role').toLowerCase() }}</option>
                    <option v-for="role in roles" v-bind:key="role" v-bind:value="role">
                        {{ role ? $t(`role_${role}`) : '' }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- WEB USER (SHOWN IF NOT ALSO CREATING WEB USER AND USER HAS EDIT_USER PERMISSION) -->
        <div class="form-group" v-if="!createWebUser && user.permissions.edit_user">
            <ValidationProvider name="drugSafeUserWebUserObject" v-slot="{ errors }">
                <label for="drugSafeUserWebUserObjectSelection">{{ $t('web_user') }}</label>
                <b-form-select
                    id="drugSafeUserWebUserObjectSelection"
                    :options="freeWebUsersPharmacyOptions"
                    @change="onChangeWebUser"
                    v-model="drugSafeUser.web_user"
                />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- LANGUAGE -->
        <div class="form-group">
            <ValidationProvider name="drugSafeUserLanguage" v-slot="{ errors }" :rules="{ required: true }">
                <label for="drugSafeUserLanguageSelection">{{ $t('language') }}</label>
                <select id="drugSafeUserLanguageSelection" class="form-control" v-model="drugSafeUser.language">
                    <option disabled>{{ $t('select') }} {{ $t('language').toLowerCase() }}</option>
                    <option v-for="lang in drugSafeLanguages" v-bind:key="lang" v-bind:value="lang">
                        {{ $t(`lang_${lang}`) }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- PIN -->
        <div class="form-group">
            <ValidationProvider
                name="drugSafeUserPin"
                vid="drugSafeUserPin"
                :rules="{
                    length: pinLength,
                    required: !edit
                }"
                v-slot="{ errors }"
            >
                <label for="drugSafeUserPinInput">{{ $t('pincode') }}</label>
                <button class="btn btn-sm btn-primary ml-sm-4" v-on:click.prevent="generateDrugSafeUserPin">
                    {{ $t('generate') }}
                </button>
                <input type="text" autocomplete="off" class="form-control" v-model="drugSafeUser.pincode" id="drugSafeUserPinInput" />
                <div class="invalid-feedback" style="display: block">
                    <span v-show="pinLength">
                        {{ $t(errors[0]) }}
                        {{ errors[0] === 'error_wrong_length' ? `${pinLength}` : null }}
                    </span>
                </div>
            </ValidationProvider>
        </div>
        <!-- PIN CONFIRM -->
        <div class="form-group">
            <ValidationProvider
                name="drugSafeUserPinConfirmation"
                :rules="{
                    required: drugSafeUser.pincode || !edit,
                    confirmed: 'drugSafeUserPin'
                }"
                v-slot="{ errors }"
                vid="drugSafeUserPinConfirmation"
            >
                <label for="drugSafeUserPinConfirmationInput">{{ $t('pincode_confirm') }}</label>
                <input
                    type="text"
                    autocomplete="off"
                    class="form-control"
                    v-model="drugSafeUser.pincode_confirmation"
                    id="drugSafeUserPinConfirmationInput"
                />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import {
    getDrugSafeLanguages,
    getFreeWebUsersForPharmacy,
    getRoles,
    getWebUser,
    handleAxiosError
} from '@/services/axiosService';
export default {
    props: {
        createWebUser: {
            required: true,
            type: Boolean
        },
        drugSafeUser: {
            required: true,
            type: Object
        },
        edit: {
            required: true,
            type: Boolean
        },
        pharmacies: {
            required: false,
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            drugSafeLanguages: [],
            error: false,
            freeWebUsersPharmacy: [],
            freeWebUsersPharmacyOptions: [],
            pinLength: 5, // pin length must be 5 digits
            roles: []
        };
    },
    async created() {
        if (!this.edit || (this.drugSafeUser.role !== 'superadmin' && this.user.permissions.edit_user)) {
            // if not editing OR current user has edit_user permission and edited user is not superadmin, get roles
            await this.getRoles();
        }
        if (!this.error) {
            // get drugsafe languages
            await this.getDrugSafeLanguages();
        }
        if (!this.error && this.edit && this.user.permissions.edit_user && this.drugSafeUser.web_user) {
            // if editing and current user has edit_user permission and edited user is associated with web user, get web user
            this.drugSafeUser.web_user_object = await this.getWebUser(this.drugSafeUser.web_user);
            this.drugSafeUser.web_user_object_original = { ...this.drugSafeUser.web_user_object };
        }
        if (!this.error && this.user.permissions.edit_user) {
            // if current user has edit_user permission, get pharmacy's free web users (i.e. web users unassociated with drugsafe users)
            await this.getFreeWebUsersForPharmacy();
            this.setFreeWebUsersPharmacyOptions();
        }
        if (!this.error) {
            // set form loaded
            this.$emit('loaded', 'form');
        }
    },
    computed: {
        ...mapState('account', ['user'])
    },
    methods: {
        // generate random five-digit drugsafe user pin
        generateDrugSafeUserPin() {
            let pass = Math.floor(Math.random() * 90000) + 10000;
            this.drugSafeUser.pincode_confirmation = this.drugSafeUser.pincode = pass.toString();
        },
        // get drugsafe languages
        async getDrugSafeLanguages() {
            try {
                this.drugSafeLanguages = await getDrugSafeLanguages();
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        async getFreeWebUsersForPharmacy() {
            try {
                if (this.drugSafeUser.pharmacy_id) {
                    this.freeWebUsersPharmacy = await getFreeWebUsersForPharmacy(this.drugSafeUser.pharmacy_id);
                }
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // get drugsafe user roles
        async getRoles() {
            try {
                this.roles = await getRoles('drugsafe');
                this.roles = this.roles.map(role => role.slug);
                if (!this.user.permissions.superadmin || this.edit) {
                    // if current user is not superadmin or target user is being edited, don't allow setting role to superadmin
                    this.roles = this.roles.filter(role => role !== 'superadmin');
                }
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // get web user by id
        async getWebUser(id) {
            try {
                return await getWebUser(id);
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // handle pharmacy change (get free web users for new pharmacy)
        async onChangePharmacy() {
            this.drugSafeUser.web_user = null;
            await this.getFreeWebUsersForPharmacy();
            this.setFreeWebUsersPharmacyOptions();
        },
        // handle web user change (get web user info)
        async onChangeWebUser() {
            this.drugSafeUser.web_user_object = await this.getWebUser(this.drugSafeUser.web_user);
        },
        // set pharmacy's free web user selection options
        setFreeWebUsersPharmacyOptions() {
            let freeWebUsersPharmacyOptions = _.sortBy(this.freeWebUsersPharmacy, ['last_name', 'first_name']);
            freeWebUsersPharmacyOptions = freeWebUsersPharmacyOptions.map(user => {
                return { value: user.id, text: `${user.first_name} ${user.last_name} (${user.email})` };
            });
            this.freeWebUsersPharmacyOptions = [
                { value: null, text: this.$t('no_selection') }, // first option: "no selection" (null value)
                ...(this.drugSafeUser.web_user_object
                    ? [
                          {
                              value: this.drugSafeUser.web_user,
                              text: `${this.drugSafeUser.web_user_object.first_name} ${this.drugSafeUser.web_user_object.last_name} (${this.drugSafeUser.web_user_object.email})`
                          }
                      ]
                    : []),
                ...freeWebUsersPharmacyOptions
            ];
        }
    }
};
</script>
